import { useState, useEffect } from "react";
import { TextField, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import {
  JOrder,
  resetForm,
  setAddItemToOrderDialog,
} from "../../../../app/slices/jomla/orderSlice";
import { NumericFormat } from "react-number-format";
import { toNumber } from "../../../../helpers/toNumber";
import { SelectJItem } from "../../../../components/Selectors/jomla/SelectJItem";
import { showNotification } from "../../../../app/slices/notificationSlice";
import useDebounce from "../../../../components/hooks/useDebounce";
import { JItems } from "../../../../app/slices/jomla/itemsSlice";
import { HiOutlineShoppingCart, HiOutlineCube } from "react-icons/hi2";
import FormDialog, {
  SectionCard,
  PlainCard,
  SwitchCard,
} from "../../../../components/FormDialog";

const AddItemToOrderForm = () => {
  const dispatch = useDispatch();

  const _JOrder = useSelector((state) => state.jOrder.addItemToOrder);
  const _JItem = useSelector((state) => state.JItems.JItemsList.data);
  const _ListSearchBody = useSelector(
    (state) => state.JItems.JItemsList.searchBody
  );

  const [loading, setLoading] = useState(false);
  const [unitPrice, setUnitPrice] = useState(0);

  const { control, reset, handleSubmit, setValue, watch } = useForm({
    defaultValues: _JOrder.form,
  });

  const initialTotalPrice = watch("total_price");
  const initialCount = watch("count");

  useEffect(() => {
    // Calculate the unit price when the component mounts or when initial values change
    if (initialCount > 0) {
      setUnitPrice(initialTotalPrice / initialCount);
    }
  }, [initialTotalPrice, initialCount]);

  const onSubmit = (data) => {
    setLoading(true);
    dispatch(
      JOrder.addItemToOrder(
        {
          j_order_id: parseInt(_JOrder.form.j_order_id),
          j_item_id: data.j_item_id,
          count: data.count,
          is_gift: Boolean(data.is_gift),
        },
        "orderDetails"
      )
    )
      .then((x) => setLoading(false))
      .catch((e) => setLoading(false));
  };

  useEffect(() => {
    // Ensure new boolean field `is_gift` defaults to false when undefined
    reset({ ..._JOrder.form, is_gift: _JOrder.form?.is_gift ?? false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_JOrder.form]);

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialog = () => {
    dispatch(setAddItemToOrderDialog({}));
  };

  const debouncedSearch = useDebounce(_ListSearchBody, 500);

  useEffect(() => {
    dispatch(JItems.getAllList(debouncedSearch));
  }, [debouncedSearch, dispatch]);

  return (
    <FormDialog
      open={_JOrder.dialog}
      onClose={() => {
        handleDialog();
      }}
      icon={<HiOutlineShoppingCart />}
      title={"اضافة منتج الى الطلب"}
      subtitle="اختر المنتج والعدد لإضافته الى الطلب"
      onSubmit={handleSubmit(onSubmit)}
      saving={loading}
      loading={_JOrder.loading}
    >
      <SectionCard icon={<HiOutlineCube />} title="المنتج">
        <Grid item xs={12}>
          <Controller
            control={control}
            name={"j_item_id"}
            render={({ field }) => (
              <SelectJItem
                {...field}
                required
                onChange={(e, newValue) => {
                  if (!newValue?.is_no_stock && newValue?.count < 1)
                    dispatch(
                      showNotification({
                        message: "العدد غير كافي لهذا العنصر",
                        type: "error",
                      })
                    );
                  else {
                    setValue("j_item_id", newValue ? newValue.id : null);
                    setValue("count", 1);
                    setValue(
                      "total_price",
                      newValue ? newValue.price * watch("count") : 0
                    );
                  }
                }}
                value={_JItem.filter((x) => x.id === field.value)[0] ?? null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            name={"count"}
            render={({ field }) => (
              <NumericFormat
                required
                thousandSeparator
                customInput={TextField}
                value={field.value}
                onChange={(e) => {
                  setValue("count", toNumber(e.target.value));
                  setValue("total_price", unitPrice * toNumber(e.target.value));
                }}
                valueIsNumericString={true}
                allowNegative={false}
                fullWidth
                label="العدد"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            name={"is_gift"}
            defaultValue={false}
            render={({ field }) => (
              <SwitchCard
                field={field}
                label="هدية"
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Grid>
      </SectionCard>
      {watch("total_price") > 0 ? (
        <PlainCard>
          <Typography variant="body">
            السعر الكلي: {watch("total_price")?.toLocaleString()} IQD
          </Typography>
        </PlainCard>
      ) : null}
    </FormDialog>
  );
};

export default AddItemToOrderForm;
